import { useState, type ReactElement } from 'react';
import {
  api,
  DELIVERY_MODES,
  type DeliveryMode,
  type DriverView,
  type UserView,
} from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { toneVars } from '../shared/lib/status';
import { useAsync } from '../shared/lib/use-async';
import { DataTable, type Column } from '../shared/ui/DataTable';
import {
  Card,
  FilterChips,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../shared/ui/primitives';
import { DriverDrawer } from './DriverDrawer';
import { DriversTable } from './DriversTable';
import { UserDrawer } from './UserDrawer';

const ROLES = ['admin', 'dispatcher', 'driver'] as const;

type RoleFilter = (typeof ROLES)[number] | 'all';

/** Параметр реестра П-И: минимальная длина пароля кабинета (NFR-922). */
const PASSWORD_MIN_PARAMETER = 'cabinet_password_min_length';

type Panel =
  | { readonly kind: 'driver'; readonly driver?: DriverView | undefined }
  | { readonly kind: 'user'; readonly user?: UserView | undefined }
  | null;

/**
 * Пользователи и их отделы (ADR-0030).
 *
 * Отдел — галочка на режим доставки: диспетчер курьерской службы видит только
 * экспресс, диспетчер дистрибуции — только опт, администратор группы — оба. Смена
 * действует сразу, без повторного входа: сервер читает отделы на каждом запросе.
 *
 * Галочка сохраняется сразу, а не кнопкой: распределение по отделам — несколько
 * щелчков на человека, и забытая кнопка «Сохранить» оставила бы диспетчера в чужом
 * отделе до первого инцидента. Остальное — имя, роль, язык, активность, пароль —
 * правится в панели справа по щелчку на строке, как в других справочниках.
 *
 * Курьеры — не отдельный раздел, а фильтр роли: это те же пользователи. На фильтре
 * «Курьеры» таблица своя: у курьера есть занятость и внешний код. Строка курьера
 * открывает панель курьера, а не пользователя: поля у них разные.
 *
 * Кнопки заведения есть только у того, кому сервер это разрешает (матрица прав,
 * разд. 2.1): спрятанная кнопка честнее неактивной.
 */
export function UsersPage({
  canEditModes,
  canCreateDrivers,
  canEditDrivers = canCreateDrivers,
  canManageUsers = false,
  currentUserId = '',
  role,
  onRole,
}: {
  readonly canEditModes: boolean;
  readonly canCreateDrivers: boolean;
  /** Правка курьера (`drivers:update`) — администратор и логист. */
  readonly canEditDrivers?: boolean | undefined;
  /** Заведение и правка пользователей кабинета — администратор. */
  readonly canManageUsers?: boolean | undefined;
  readonly currentUserId?: string | undefined;
  readonly role: string | null;
  readonly onRole: (role: string | null) => void;
}): ReactElement {
  const messages = useMessages();
  const users = useAsync(() => api.users(), []);
  const drivers = useAsync(() => api.drivers(), []);
  // Политика пароля нужна только форме пользователя: без права её не спрашиваем.
  const parameters = useAsync(
    async () => (canManageUsers ? api.parameters() : []),
    [canManageUsers],
  );
  const [panel, setPanel] = useState<Panel>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const filter: RoleFilter = ROLES.includes(role as (typeof ROLES)[number])
    ? (role as RoleFilter)
    : 'all';
  const all = users.data?.data ?? [];
  const rows = filter === 'all' ? all : all.filter((user) => user.role === filter);
  const [error, setError] = useState<string | null>(null);

  const passwordMin = (() => {
    const value = (parameters.data ?? []).find(
      (item) => item.key === PASSWORD_MIN_PARAMETER,
    )?.value;

    return typeof value === 'number' ? value : null;
  })();

  // Запись курьера и учётная запись пользователя — разные сущности с разными id;
  // связывает их телефон: он уникален и служит логином.
  const driversByPhone = new Map(
    (drivers.data?.data ?? []).map((driver) => [driver.phone ?? '', driver]),
  );

  const open = (next: Panel): void => {
    setNotice(null);
    setPanel(next);
  };

  // Новый курьер должен быть виден сразу: экран переходит на фильтр курьеров и
  // перечитывает оба списка — иначе счётчик на чипе разойдётся с таблицей.
  const onDriverCreated = (driver: { readonly full_name: string }): void => {
    setPanel(null);
    setNotice(`${messages.cabinet.drivers.created}: ${driver.full_name}`);
    users.reload();
    drivers.reload();
    onRole('driver');
  };

  const onDriverSaved = (driver: { readonly full_name: string }): void => {
    setPanel(null);
    setNotice(`${messages.cabinet.drivers.saved}: ${driver.full_name}`);
    users.reload();
    drivers.reload();
  };

  const onUserSaved = (user: UserView | null, kind: 'created' | 'saved' | 'password'): void => {
    setPanel(null);
    setNotice(`${messages.cabinet.userForm.notices[kind]}: ${user?.full_name ?? ''}`);
    users.reload();
  };

  const toggle = async (user: UserView, mode: DeliveryMode): Promise<void> => {
    setError(null);

    const modes = user.modes.includes(mode)
      ? user.modes.filter((item) => item !== mode)
      : [...user.modes, mode];

    try {
      await api.setUserModes(user.id, modes);
      users.reload();
    } catch {
      setError(messages.cabinet.users.saveFailed);
    }
  };

  /** Строка общего списка: курьер — в панель курьера, остальные — в панель пользователя. */
  const openRow = (row: UserView): void => {
    if (row.role === 'driver') {
      const driver = driversByPhone.get(row.phone);

      if (canEditDrivers && driver !== undefined) {
        open({ kind: 'driver', driver });
      }

      return;
    }

    if (canManageUsers) {
      open({ kind: 'user', user: row });
    }
  };

  const rowsOpen = canManageUsers || canEditDrivers;

  const columns: readonly Column<UserView>[] = [
    { key: 'name', title: messages.cabinet.users.name, render: (row) => row.full_name },
    {
      key: 'phone',
      title: messages.cabinet.users.phone,
      width: '160px',
      render: (row) => <span className="num">{row.phone}</span>,
    },
    {
      key: 'role',
      title: messages.cabinet.users.role,
      width: '140px',
      render: (row) => messages.cabinet.users.roles[row.role],
    },
    {
      key: 'modes',
      title: messages.cabinet.users.departments,
      width: '360px',
      render: (row) =>
        !canEditModes ? (
          row.modes.map((mode) => messages.cabinet.modes[mode]).join(' · ') ||
          messages.cabinet.users.noDepartment
        ) : (
          // Щелчок по галочке не открывает панель строки.
          <div
            role="presentation"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}
          >
            {DELIVERY_MODES.map((mode) => (
              <label
                key={mode}
                style={{ display: 'flex', gap: 'var(--sp-1)', alignItems: 'center' }}
              >
                <input
                  type="checkbox"
                  checked={row.modes.includes(mode)}
                  onChange={() => void toggle(row, mode)}
                />
                <span>{messages.cabinet.modes[mode]}</span>
              </label>
            ))}
            {row.modes.length === 0 ? (
              <span style={{ color: 'var(--warn)' }}>{messages.cabinet.users.noDepartment}</span>
            ) : null}
          </div>
        ),
    },
    {
      key: 'active',
      title: messages.cabinet.drivers.status,
      width: '120px',
      render: (row) => {
        const vars = toneVars(row.is_active ? 'ok' : 'neutral');

        return (
          <span style={{ ...statusStyle, color: vars.color, background: vars.background }}>
            {row.is_active
              ? messages.cabinet.drivers.statusActive
              : messages.cabinet.drivers.statusInactive}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)', minWidth: 0 }}>
      <header style={headerStyle}>
        <h1>{messages.cabinet.nav.users}</h1>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          {canManageUsers ? (
            <button
              type="button"
              onClick={() => open({ kind: 'user' })}
              style={filter === 'driver' ? secondaryButtonStyle : primaryButtonStyle}
            >
              {messages.cabinet.userForm.add}
            </button>
          ) : null}
          {canCreateDrivers ? (
            <button
              type="button"
              onClick={() => open({ kind: 'driver' })}
              style={
                canManageUsers && filter !== 'driver' ? secondaryButtonStyle : primaryButtonStyle
              }
            >
              {messages.cabinet.drivers.add}
            </button>
          ) : null}
        </div>
      </header>

      {notice === null ? null : (
        <div role="status">
          <Card tone="ok">{notice}</Card>
        </div>
      )}

      <FilterChips
        active={filter}
        onSelect={(code) => onRole(code === 'all' ? null : code)}
        items={(['all', ...ROLES] as const).map((code) => ({
          code,
          label: messages.cabinet.users.filter[code],
          count: code === 'all' ? all.length : all.filter((user) => user.role === code).length,
        }))}
      />

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}

      {filter === 'driver' ? (
        <>
          {drivers.error === null ? null : (
            <div role="alert" style={{ color: 'var(--danger)' }}>
              {`${messages.cabinet.common.error}: ${drivers.error.code}`}
            </div>
          )}
          <DriversTable
            rows={drivers.data?.data ?? []}
            loading={drivers.loading && drivers.data === null}
            onCreate={canCreateDrivers ? () => open({ kind: 'driver' }) : undefined}
            onOpen={canEditDrivers ? (driver) => open({ kind: 'driver', driver }) : undefined}
            selectedId={panel?.kind === 'driver' ? (panel.driver?.id ?? null) : null}
          />
        </>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          onRowClick={rowsOpen ? openRow : undefined}
          selectedKey={panel?.kind === 'user' ? (panel.user?.id ?? null) : null}
          rail={(row) => (row.is_active ? 'neutral' : 'wait')}
          emptyText={
            users.loading && users.data === null
              ? messages.cabinet.common.loading
              : messages.cabinet.common.empty
          }
        />
      )}

      {panel?.kind === 'driver' ? (
        <DriverDrawer
          driver={panel.driver}
          onClose={() => setPanel(null)}
          onCreated={onDriverCreated}
          onSaved={onDriverSaved}
        />
      ) : null}

      {panel?.kind === 'user' ? (
        <UserDrawer
          user={panel.user}
          currentUserId={currentUserId}
          passwordMinLength={passwordMin}
          onClose={() => setPanel(null)}
          onSaved={onUserSaved}
        />
      ) : null}
    </div>
  );
}

const headerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-3)',
  justifyContent: 'space-between',
  alignItems: 'center',
} as const;

const statusStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 'var(--tag-h)',
  padding: '0 var(--sp-2)',
  borderRadius: 'var(--r-xs)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 'var(--fw-bold)',
  whiteSpace: 'nowrap',
} as const;
